import { useShallow } from "zustand/react/shallow";

import { ErrorCircleColored } from "components/icons/generated";
import Box from "ds/components/Box";
import CircularProgress from "ds/components/CircularProgress";
import Icon from "ds/components/Icon";
import Tooltip from "ds/components/Tooltip";
import Typography from "ds/components/Typography";
import { formatCompactNumber } from "utils/number";

import { selectBudgetResetsIn, selectIsBudgetExhausted } from "../store/selectors";
import useChatStore, { ChatBudgetUsage } from "../store/useStore";
import styles from "./styles.module.css";

const getBudgetProgress = (budgetUsage: ChatBudgetUsage | undefined): number => {
  if (!budgetUsage) return 100;

  const { usedTokens, totalTokens } = budgetUsage;
  const remaining = Math.round(((totalTokens - usedTokens) / totalTokens) * 100);

  return remaining;
};

const ChatBudgetProgress = () => {
  const budgetUsage = useChatStore(useShallow((state) => state.budgetUsage));

  if (budgetUsage?.totalTokens === -1) {
    return null;
  }

  const isExhausted = selectIsBudgetExhausted(budgetUsage);
  const progress = getBudgetProgress(budgetUsage);

  return (
    <Tooltip
      widthMode="maxWidthSm"
      on={(props) => (
        <span {...props} className={styles.wrapper}>
          {isExhausted ? (
            <Icon src={ErrorCircleColored} />
          ) : (
            <CircularProgress variant="subdued" state="in-progress" progress={progress} />
          )}
        </span>
      )}
    >
      {budgetUsage ? (
        <Box direction="column" gap="small" align="center">
          <Typography tag="p" variant="p-body3">
            Tokens left:{" "}
            {formatCompactNumber(Math.max(0, budgetUsage.totalTokens - budgetUsage.usedTokens))} /{" "}
            {formatCompactNumber(budgetUsage.totalTokens)}
          </Typography>
          <Typography tag="p" variant="p-body3">
            Resets in: {selectBudgetResetsIn(budgetUsage)}
          </Typography>
        </Box>
      ) : (
        <Typography tag="p" variant="p-body3">
          Token usage will be calculated after starting the dialogue
        </Typography>
      )}
    </Tooltip>
  );
};

export default ChatBudgetProgress;
